import { useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { RefreshCw, RefreshCwOff } from 'lucide-react'
import { toast } from 'sonner'

import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { Button } from '@fortem-monorepo/react-ui'
import { Form } from '@fortem-monorepo/react-ui'
import { TradesSchemas } from '@fortem-monorepo/web-sdk'

import { FormValues } from '@/pages/funds/ic-guidelines-v2/components/edit-guideline-form/FormValues'
import { BookTradeFormFieldsV2 } from '@/pages/trading/new-trade/components/BookTradeFormFieldsV2'
import { BookTradeSummary } from '@/pages/trading/new-trade/components/BookTradeSummary'
import { useBookTradeMutation } from '@/react-query/mutations/trades'

const useInvalidateBookTradeQueries = () => {
  const queryClient = useQueryClient()

  const invalidate = async () => {
    const queryKeys = [['instruments'], ['static']]
    await Promise.all(queryKeys.map((queryKey: any) => queryClient.invalidateQueries({ queryKey })))
    toast.success('Resfreshed all dropdown data')
  }

  return invalidate
}

export interface BookTradeFormV2Props {}

export const BookTradeFormV2: React.FC<BookTradeFormV2Props> = ({}) => {
  const form = useFormWithSchema(
    {
      mode: 'all',
      defaultValues: {
        price_format: 'decimal',
        locked_ytm: null,
        accrued_interests: null,
        unit_multiplier: 1,
        tags: [],
      },
    },
    TradesSchemas.bookTradeSchema,
  )
  const invalidateBookTradeQueries = useInvalidateBookTradeQueries()

  const {
    watch,
    reset,
    formState: { isValid, isSubmitting },
  } = form

  const values = watch()

  const { isDeveloper } = useUserContext()

  // Reset fields on instrument_type_id change
  useEffect(() => {}, [values.instrument_type_id])

  // Reset fields on strategy_type change
  useEffect(() => {
    const values = form.getValues()

    reset({
      ...values,
    })
  }, [values.strategy_type])

  const { Dialog: BookTradeModal, withConfirmation: confirmBookTrade } = useConfirmModal({
    title: 'Book Trade',
    description: `You are about the submit a trade to the database.`,
  })

  const bookTradeMutation = useBookTradeMutation()

  const handleSubmit = form.handleSubmit(async (data) => {
    return bookTradeMutation.mutateAsync(data)
  })

  return (
    <Form {...form}>
      <BookTradeModal>
        <BookTradeSummary />
      </BookTradeModal>
      {isDeveloper() && (
        <div className="p-4 border rounded-md bg-gray-100 flex flex-col gap-4 mb-4">
          <FormValues />
        </div>
      )}
      <div className={'border bg-gray-50 rounded-md max-w-400'}>
        <div className="py-2 px-1 border-b">
          <Button
            type="button"
            variant="link"
            onClick={() => {
              form.reset()
              form.trigger()
            }}
          >
            <RefreshCwOff /> Reset Form
          </Button>
          <Button
            role="button"
            variant={'link'}
            onClick={async (e) => {
              e.preventDefault()
              await invalidateBookTradeQueries()
            }}
          >
            <RefreshCw />
            Refresh dropdown data
          </Button>
        </div>
        <div className="@container p-4 space-y-4">
          <BookTradeFormFieldsV2 />
          <div className={'flex gap-2'}>
            <Button
              type="button"
              disabled={isSubmitting || !isValid}
              className={'flex-1'}
              onClick={() => confirmBookTrade(() => handleSubmit())}
            >
              Book trade
            </Button>
          </div>
        </div>
      </div>
    </Form>
  )
}
